/**
 * The admin console's shapes (0.8.0), shared by the API and the screens: users and their extras, the
 * settings rows, the review queue, the audit viewer, Fix it, the diagnostic bundle.
 */
import type { Role } from './roles';
import type { HealthCheck } from './api-types';
import { defaultCallMode, type CallMode, type CallingSettings } from './calling';
import type { MySettings } from './my-settings';

/* ---------------- Users ---------------- */

export type CrewAssignment = 'A' | 'B';

/** What the app keeps per person beside the SWA role (the role itself is Elijah's, set in Azure). */
export interface UserExtras {
  /** May see completions, incidents and sensitive audit events. Default: administrators yes, others no. */
  sensitive: boolean;
  /** May press Approve surge / Revoke. Default: administrators yes. Only administrators can use it (NEEDS-ELI 20). */
  surgeApprover: boolean;
  /** The language the app opens in for this person until they change it under the gear. */
  language: 'en' | 'es' | null;
  /** Crew accounts: which Classico crew (the crew view shows it). */
  crew: CrewAssignment | null;
  /** 1.5.7: how the app's call links dial for this person (normal · hide my number, *67 · through Teams). Their own switch, or the administrator's. */
  callMode: CallMode;
}

export type SignInOutcome = 'ok' | 'no-role' | 'domain';

export interface UserView {
  email: string;
  userId: string | null;
  /** The role the person carried the last time they reached the app (null: none yet). */
  role: Role | null;
  roles: string[];
  firstSeen: string | null;
  lastSeen: string | null;
  lastAttempt: { at: string; outcome: SignInOutcome } | null;
  /** Recorded when the administrator used "Invite someone" (the invitation itself is made in Azure). */
  invited: { role: Role; at: string; by: string } | null;
  extras: UserExtras;
  /** Whether each extra is the default (not set by the administrator). */
  defaults: Record<keyof UserExtras, boolean>;
  /** 1.5.13: the app's own service identity (the account the processor runs as) — not a person: no role, no switches. */
  service?: boolean;
}

export interface UsersResponse {
  users: UserView[];
  /** The SWA resource and host names for the invitation steps. */
  swa: { resource: string; host: string };
}

export interface MeResponse {
  email: string;
  role: Role | null;
  extras: UserExtras;
  /**
   * 1.5.7: the person's calling choice as it applies now (Teams only while Teams Phone is set up) and whether
   * Teams is offered. null when the app's store could not be read — the screen then uses the choice kept on the device.
   */
  calling: CallingSettings | null;
  /**
   * 1.5.16: the person's own settings as the server keeps them (language, appearance, text size, the last program
   * tab, the last Admin page). null: they have never saved any. Left out when the store could not be read — the
   * screen then keeps what the device has and sends nothing.
   */
  settings?: MySettings | null;
}

export function defaultExtras(role: Role | null): UserExtras {
  // 1.8.3: the crew's calls hide their number until they choose otherwise (shared/calling.ts `defaultCallMode`)
  return { sensitive: role === 'admin', surgeApprover: role === 'admin', language: null, crew: null, callMode: defaultCallMode(role) };
}

/** The exact message a person sees at each point of signing in (the "Someone can't sign in" check). */
export type SignInState = 'never' | 'invited-not-opened' | 'no-role' | 'domain' | 'ok';

/* ---------------- Settings ---------------- */

export type SettingValueType = 'Text' | 'Number' | 'Yes/No' | 'Time' | 'Email';

export interface SettingRowView {
  id: number;
  key: string;
  name: string;
  value: string;
  type: SettingValueType | string;
  category: string;
  description: string;
  active: boolean;
  order: number | null;
}

export interface SettingsResponse {
  rows: SettingRowView[];
  /** 1.4.2: which Azure app settings are set (TRANSLATOR_*, AIRTABLE_TOKEN) — never their values. */
  env?: Record<string, boolean>;
}

/** The settings with a card of their own (the "every other row" list leaves them out). */
export const CARD_SETTING_PREFIXES = ['DAY_PLAN_', 'HK_WARN_AT', 'HK_DEEP_', 'EMAIL_', 'MAIL_', 'TENANT_RESCHEDULE_', 'OFFICE_PHONE', 'BUILDING_MANAGER_', 'COMPANION_', 'VISIONS_', 'ASSISTANT_'] as const;

export function hasOwnCard(key: string): boolean {
  if (key in RETIRED_SETTINGS) return false;
  return CARD_SETTING_PREFIXES.some((p) => key.startsWith(p));
}

/** A value checked against its ValueType before it is written. */
export function validSettingValue(type: string, value: string): boolean {
  const v = value.trim();
  switch (type) {
    case 'Number':
      return v === '' || /^-?\d+(\.\d+)?$/.test(v);
    case 'Yes/No':
      return /^(yes|no|true|false)$/i.test(v);
    case 'Time':
      return v === '' || /^([01]?\d|2[0-3]):[0-5]\d$/.test(v);
    case 'Email':
      return v === '' || v === 'Not set' || v.split(/[;,]/).map((s) => s.trim()).filter(Boolean).every((s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s));
    default:
      return v.length <= 4000;
  }
}

/**
 * The Companion card's rows (docs/DESIGN-DECISIONS-v4.md: training date and trainer, the VISIONS liaison).
 * 1.4.1: the training is defined once — the three rows the volunteer welcome email reads ([TrainingDate]
 * [TrainingTime] [TrainerName], category Communications). The card shows date and time together as "Next
 * volunteer training".
 */
export const COMPANION_SETTINGS = [
  { key: 'COMPANION_TRAINING_DATE', name: 'Next VISIONS training: date ([TrainingDate])', type: 'Text', description: 'The volunteer welcome email names the next human-guide training.' },
  { key: 'COMPANION_TRAINING_TIME', name: 'Next VISIONS training: time ([TrainingTime])', type: 'Text', description: 'The time of that training.' },
  { key: 'COMPANION_TRAINER_NAME', name: 'Who leads the training ([TrainerName])', type: 'Text', description: 'The VISIONS trainer named in the welcome email.' },
  { key: 'VISIONS_LIAISON_EMAIL', name: 'VISIONS liaison email', type: 'Email', description: 'Where the monthly Exhibit A report goes after a person reviews it (1.2.0). Nothing is sent automatically.' },
] as const;

/**
 * Rows the app no longer reads (1.4.1). They stay on the site (never a delete); Settings lists them as
 * "no longer used" with what replaced them, read-only, and nothing offers to create them again.
 */
export const RETIRED_SETTINGS: Readonly<Record<string, string>> = {
  COMPANION_NEXT_TRAINING_DATE: 'COMPANION_TRAINING_DATE',
  COMPANION_TRAINER: 'COMPANION_TRAINER_NAME',
};

/* ---------------- Review queue ---------------- */

export type QueueKey = 'q1' | 'q2' | 'q3';

export const QUEUE_LISTS: Record<QueueKey, string> = {
  q1: 'SM_AppointmentTransactions',
  q2: 'SM_HousekeepingCapacityTransactions',
  q3: 'SM_ServiceCompletionTransactions',
};

export interface ReviewRow {
  queue: QueueKey;
  id: number;
  transactionId: string;
  operation: string;
  processingStatus: string;
  recoveryStatus: string;
  outcomeCode: string | null;
  /** The processor's own words, verbatim. */
  message: string | null;
  submitted: string;
  requestedBy: string;
  /** The record it is about: appointment, slot or completion. */
  record: { kind: 'appointment' | 'slot' | 'completion' | 'tenant'; id: string; dayKey: string | null; tenantId: string | null } | null;
  attempts: number;
}

export interface ReviewResponse {
  rows: ReviewRow[];
  counts: Record<QueueKey, number>;
}

/* ---------------- Audit ---------------- */

export interface AuditRow {
  source: 'list' | 'app' | 'changes';
  at: string;
  code: string;
  category: string;
  outcome: string;
  severity: string;
  who: string;
  recordId: string;
  summary: string;
  sensitive: boolean;
}

export interface AuditResponse {
  rows: AuditRow[];
  /** How many sensitive rows were left out for this person. */
  hiddenSensitive: number;
  categories: string[];
}

/* ---------------- Fix it ---------------- */

export const FIX_TOOLS = [
  'resend',
  'relink',
  'release',
  'retry',
  'reopen',
  'move',
  'restore',
  'regen',
  'signin',
  'vollink',
  'closeout',
  'stuck',
  'household',
] as const;
export type FixTool = (typeof FIX_TOOLS)[number];

/** Which kind of record each tool acts on (the "Which record" search). */
export const FIX_RECORD: Record<FixTool, 'message' | 'appointment' | 'hold' | 'transaction' | 'closed' | 'cycle' | 'user' | 'volunteer' | 'closeout' | 'pair' | 'none'> = {
  resend: 'message',
  relink: 'appointment',
  release: 'hold',
  retry: 'transaction',
  reopen: 'closed',
  move: 'appointment',
  restore: 'none',
  regen: 'cycle',
  signin: 'user',
  vollink: 'volunteer',
  closeout: 'closeout',
  stuck: 'none',
  household: 'pair',
};

export interface FixCandidate {
  id: string;
  label: string;
  state: string;
}

export interface FixResult {
  ok: boolean;
  /** Plain words for the toast (the processor's own words where a queue decided). */
  message: string;
  detail?: Record<string, unknown>;
}

/** Fix it → Write the missing links (1.5.10): how many links the lists still owe, before anything is written. */
export interface MissingLinksCount {
  /** People on the list (the run goes through all of them, a few at a time). */
  people: number;
  /** Folder links and release links whose column is empty though the app holds the link. */
  folder: number;
  release: number;
  /** Text columns not on the site yet (scripts/add-link-text-columns.ps1) — nothing can be written until they are. */
  columnsMissing: string[];
}

export interface MissingLinksStep {
  /** People looked at so far, of `people`. */
  done: number;
  people: number;
  written: number;
  failed: number;
  /** Where the next step of the loop starts; null when the run is finished. */
  next: number | null;
}

/** Fix it → Write the missing home visit rows (1.5.12): what the list still owes, before anything is written. */
export interface MissingHomeVisitRowsCount {
  /** SM_HomeVisits is not on the site yet (scripts/add-home-visits-list.ps1) — nothing can be written until it is. */
  listMissing: boolean;
  /** Home visits on record in the app: the stored records, and the visits recorded before the checklist that have their PDF. */
  records: number;
  /** Of those, how many have no row. */
  missing: number;
  already: number;
  /** Home visit ticks with no document filed by the app (imported): not recorded in the app, so no row is written. */
  noRecord: number;
}

export interface MissingHomeVisitRowsResult {
  listMissing: boolean;
  written: number;
  already: number;
  /** Rows the site refused (Health → Saving has the site's words). */
  failed: number;
}

/* ---------------- Diagnostics ---------------- */

export const DIAGNOSTIC_PREAMBLE =
  'You are debugging the Selis Manor Scheduler (repo selis-scheduler). Read CLAUDE.md and STATUS.md. The health check below failed. Find the cause, fix it in source, add a test that fails first, and release it as the next patch version.';

export interface DiagnosticBundle {
  preamble: string;
  generatedAt: string;
  environment: string;
  version: { app: string; sha: string; builtAt: string; node: string };
  checks: HealthCheck[];
  /** The failing (or chosen) check, when the bundle is for one. */
  check: HealthCheck | null;
  config: Record<string, string>;
  log: { at: string; level: string; event: string; text: string }[];
}

export interface AboutResponse {
  version: string;
  sha: string;
  builtAt: string;
  environment: string;
  driver: 'mock' | 'graph';
  links: { repo: string; site: string | null; swa: string | null; app: string | null };
}
